import {
  Component,
  ChangeDetectorRef,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  Output,
  signal,
  ViewChild,
} from '@angular/core';
import type { ReadResult } from 'zxing-wasm/reader';
import { DataService } from '../../service/DataService/data.service';
import { ScanCandidate, ScanConfirmationTracker } from './scan-confirmation-tracker';
import { clampScannerZoom, resolveInitialScannerZoom } from './scanner-zoom';
import { ScannerReaderOptionsFallback } from './scanner-reader-options';
import { ScannerSuccessSound } from './scanner-success-sound';
import {
  appendConfirmedIdentifier,
  createAddRelatedPartPayload,
  displayConfirmedIdentifiersNewestFirst,
  RelatedPartRegistrationRequest,
} from './related-part-registration';
import { CodeSearchSession } from './code-search-session';

export type LiveCodeScannerMode = 'register' | 'search' | 'collect';

export interface CollectedScan {
  readonly value: string;
  readonly format?: string;
}

type ScannerPhase =
  | 'camera-start'
  | 'video-play'
  | 'decoder-init'
  | 'decode'
  | 'visibility-resume'
  | 'camera-restart'
  | 'camera-stop'
  | 'zoom'
  | 'add-related-part'
  | 'page-leave'
  | 'component-destroy';

type BarcodeReader = (input: ImageData, options?: import('zxing-wasm/reader').ReaderOptions) => Promise<ReadResult[]>;

interface NumericMediaCapability {
  min: number;
  max: number;
  step?: number;
}

interface ScannerMediaTrackCapabilities extends MediaTrackCapabilities {
  zoom?: NumericMediaCapability;
}

interface ScannerMediaTrackSettings extends MediaTrackSettings {
  zoom?: number;
}

interface ScannerZoomConstraint extends MediaTrackConstraintSet {
  zoom: number;
}

interface ScannerDiagnostics {
  cameraState: string;
  videoReadyState: number;
  videoWidth: number;
  videoHeight: number;
  streamActive: boolean;
  trackReadyState: string;
  lastDecodeAt: string;
  lastDetectionAt: string;
  decodeCount: number;
  decodeErrorCount: number;
  lastDecodeDurationMs: number;
  averageDecodeDurationMs: number;
  lastErrorName: string;
  lastErrorMessage: string;
  restartCount: number;
  suppressedErrorCount: number;
  lastLifecycleEvent: string;
  zoomCapability: string;
  zoomCurrent: string;
}

@Component({
  standalone: false,
  selector: 'app-live-code-scanner',
  templateUrl: './live-code-scanner.component.html',
  styleUrls: ['./live-code-scanner.component.scss'],
})
export class LiveCodeScannerComponent implements OnDestroy {
  @Input() isOpen = false;
  @Input() mode: LiveCodeScannerMode = 'register';
  @Input() jobId: number | null = null;
  @Input() initialScans: readonly CollectedScan[] = [];
  @Output() closed = new EventEmitter<void>();
  @Output() relatedPartRegistered = new EventEmitter<string | undefined>();
  @Output() identifierConfirmed = new EventEmitter<string>();
  @Output() identifierScanned = new EventEmitter<{ value: string; format?: ReadResult['format'] }>();
  @Output() collectionCompleted = new EventEmitter<readonly CollectedScan[]>();

  @ViewChild('scannerVideo') scannerVideo?: ElementRef<HTMLVideoElement>;
  @ViewChild('scannerCanvas') scannerCanvas?: ElementRef<HTMLCanvasElement>;

  readonly scannedCodes = signal<readonly string[]>([]);
  readonly isRegistering = signal(false);
  readonly registrationError = signal('');
  scanCandidates: readonly ScanCandidate[] = [];
  readonly lifecycleEvents: string[] = [];
  readonly diagnostics: ScannerDiagnostics = {
    cameraState: '停止中',
    videoReadyState: 0,
    videoWidth: 0,
    videoHeight: 0,
    streamActive: false,
    trackReadyState: 'なし',
    lastDecodeAt: '-',
    lastDetectionAt: '-',
    decodeCount: 0,
    decodeErrorCount: 0,
    lastDecodeDurationMs: 0,
    averageDecodeDurationMs: 0,
    lastErrorName: '-',
    lastErrorMessage: '-',
    restartCount: 0,
    suppressedErrorCount: 0,
    lastLifecycleEvent: '-',
    zoomCapability: '未確認',
    zoomCurrent: '-',
  };

  userErrorMessage = '';
  hasRecentDetection = false;
  zoomSupported = false;
  zoomMin = 1;
  zoomMax = 1;
  zoomStep = 0.1;
  zoomCurrent = 1;

  private readonly scannedCodeSet = new Set<string>();
  private readonly scannedCodeFormats = new Map<string, ReadResult['format']>();
  private readonly confirmationTracker = new ScanConfirmationTracker(2, 1_000);
  private readonly registrationRequest = new RelatedPartRegistrationRequest();
  private readonly codeSearchSession = new CodeSearchSession();
  private readonly readerOptionsFallback = new ScannerReaderOptionsFallback();
  private readonly successSound = new ScannerSuccessSound();
  private readonly errorRateLimitMs = 30_000;
  private readonly decodeIntervalMs = 250;
  private readonly maxDecodeDimension = 1280;
  private readonly zoomStorageKey = 'pc119mobile.liveCodeScanner.zoom';
  private readonly recentErrors = new Map<string, number>();

  private stream?: MediaStream;
  private readBarcodes?: BarcodeReader;
  private decodeTimer?: number;
  private visibilityCheckTimer?: number;
  private detectionFeedbackTimer?: number;
  private scannerSession = 0;
  private decodeInProgress = false;
  private isStopping = false;
  private totalDecodeDurationMs = 0;
  private lastVideoTime = -1;
  private stalledFrameCount = 0;
  private blackFrameCount = 0;
  private closeRequested = false;

  constructor(
    private dataService: DataService,
    private changeDetector: ChangeDetectorRef,
  ) {}

  async scannerDidPresent(): Promise<void> {
    void this.successSound.prepare();
    this.closeRequested = false;
    this.readerOptionsFallback.reset();
    this.codeSearchSession.reset();
    this.registrationError.set('');
    this.clearResults();
    this.restoreInitialScans();
    await this.startScanner('camera-start');
  }

  async scannerDidDismiss(): Promise<void> {
    if (!this.closeRequested) {
      await this.cancelScanner();
      return;
    }
    await this.stopScanner('camera-stop');
  }

  async cancelScanner(): Promise<void> {
    if (this.isRegistering()) {
      return;
    }
    await this.closeAndDiscardResults();
  }

  clearResults(): void {
    this.scannedCodeSet.clear();
    this.scannedCodeFormats.clear();
    this.confirmationTracker.clear();
    this.scannedCodes.set([]);
    this.scanCandidates = [];
    this.changeDetector.markForCheck();
  }

  getScannedCodeFormat(code: string): ReadResult['format'] | undefined {
    return this.scannedCodeFormats.get(code);
  }

  get displayedScannedCodes(): readonly string[] {
    return displayConfirmedIdentifiersNewestFirst(this.scannedCodes());
  }

  get collectedScans(): readonly CollectedScan[] {
    return this.scannedCodes().map(value => ({ value, format: this.getScannedCodeFormat(value) }));
  }

  removeScannedCode(code: string, sliding?: { close: () => Promise<void> }): void {
    this.scannedCodeSet.delete(code);
    this.scannedCodeFormats.delete(code);
    this.confirmationTracker.forget(code);
    this.scannedCodes.update(current => current.filter(currentCode => currentCode !== code));
    void sliding?.close();
    this.changeDetector.markForCheck();
  }

  async registerRelatedPart(): Promise<void> {
    const identifiers = this.scannedCodes();
    if (this.isRegistering() || identifiers.length === 0 || !Number.isFinite(this.jobId)) {
      return;
    }

    this.isRegistering.set(true);
    this.registrationError.set('');
    const payload = createAddRelatedPartPayload(this.jobId!, identifiers);

    try {
      const response = await this.registrationRequest.submit(
        payload,
        requestPayload => this.dataService.addRelatedPart(
          requestPayload.jobId,
          requestPayload.identifiers,
        ),
      );
      if (response.status !== 'success') {
        throw new Error(`addRelatedPart failed: ${String(response.detail ?? response.status)}`);
      }

      this.clearResults();
      this.relatedPartRegistered.emit(response.memopad);
      await this.closeScannerAfterRegistration();
    } catch (error) {
      this.registrationError.set('登録に失敗しました。もう一度お試しください。');
      this.recordScannerError('add-related-part', error);
    } finally {
      this.isRegistering.set(false);
      this.changeDetector.markForCheck();
    }
  }

  async completeCollection(): Promise<void> {
    if (this.closeRequested) return;
    this.closeRequested = true;
    await this.stopScanner('camera-stop');
    this.collectionCompleted.emit(this.collectedScans);
    this.closed.emit();
  }

  async onZoomChange(event: Event): Promise<void> {
    const value = (event as CustomEvent<{ value: number }>).detail.value;
    await this.applyZoom(value, true);
  }

  async restartScanner(): Promise<void> {
    this.diagnostics.restartCount++;
    this.userErrorMessage = '';
    this.changeDetector.markForCheck();
    await this.stopScanner('camera-restart');
    if (this.isOpen) {
      await this.startScanner('camera-restart');
    }
  }

  async stopScanner(phase: ScannerPhase = 'camera-stop'): Promise<void> {
    this.isStopping = true;
    this.scannerSession++;
    this.clearTimers();
    this.decodeInProgress = false;
    this.diagnostics.cameraState = '停止処理中';

    try {
      const video = this.scannerVideo?.nativeElement;
      if (video) {
        video.pause();
        video.srcObject = null;
      }
      if (this.stream) {
        for (const track of this.stream.getTracks()) {
          track.stop();
        }
      }
    } catch (error) {
      this.recordScannerError(phase === 'component-destroy' ? phase : 'camera-stop', error);
    } finally {
      this.stream = undefined;
      this.resetZoomCapability();
      this.lastVideoTime = -1;
      this.stalledFrameCount = 0;
      this.blackFrameCount = 0;
      this.diagnostics.cameraState = '停止中';
      this.updateMediaDiagnostics();
      this.isStopping = false;
      this.changeDetector.markForCheck();
    }
  }

  onVideoPlaying(): void {
    this.diagnostics.cameraState = '再生中';
    this.updateMediaDiagnostics();
    this.changeDetector.markForCheck();
  }

  onVideoPlaybackProblem(eventName: string): void {
    if (!this.isOpen || this.isStopping) {
      return;
    }
    this.recordLifecycleEvent(`video:${eventName}`);
    this.recordScannerError('video-play', new Error(`video ${eventName}`), 'warning');
  }

  @HostListener('document:visibilitychange')
  onVisibilityChange(): void {
    if (!this.isOpen) {
      return;
    }

    this.recordLifecycleEvent(`visibility:${document.visibilityState}`);
    this.updateMediaDiagnostics();

    if (document.visibilityState === 'hidden' && this.mode === 'search') {
      void this.cancelScanner();
      return;
    }

    if (document.visibilityState === 'visible') {
      if (this.visibilityCheckTimer !== undefined) {
        window.clearTimeout(this.visibilityCheckTimer);
      }
      this.visibilityCheckTimer = window.setTimeout(() => {
        this.updateMediaDiagnostics();
        const video = this.scannerVideo?.nativeElement;
        const track = this.stream?.getVideoTracks()[0];
        if (!this.stream?.active || track?.readyState !== 'live' || !video || video.readyState < 2 || video.paused) {
          this.recordScannerError(
            'visibility-resume',
            new Error('visibility復帰後にカメラまたはvideoが正常状態へ復帰していません'),
          );
        }
        this.changeDetector.markForCheck();
      }, 500);
    }
  }

  @HostListener('window:pagehide')
  onPageHide(): void {
    if (this.isOpen && this.mode === 'search') {
      void this.cancelScanner();
    }
  }

  ngOnDestroy(): void {
    void this.stopScanner('component-destroy');
    this.successSound.close();
  }

  private async startScanner(phase: 'camera-start' | 'camera-restart'): Promise<void> {
    await this.stopScanner(phase);
    if (!this.isOpen) {
      return;
    }

    const session = ++this.scannerSession;
    let currentPhase: ScannerPhase = phase;
    this.userErrorMessage = '';
    this.diagnostics.cameraState = 'カメラ起動中';
    this.changeDetector.markForCheck();

    try {
      if (!window.isSecureContext) {
        throw new DOMException('カメラはHTTPSの安全な接続でのみ使用できます', 'SecurityError');
      }
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new DOMException('このブラウザはgetUserMediaに対応していません', 'NotSupportedError');
      }

      const requestedStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: false,
      });
      if (session !== this.scannerSession || !this.isOpen) {
        requestedStream.getTracks().forEach(track => track.stop());
        return;
      }
      this.stream = requestedStream;
      this.attachStreamObservers(this.stream);
      await this.configureZoomCapability(this.stream.getVideoTracks()[0]);

      currentPhase = 'video-play';
      const video = this.scannerVideo?.nativeElement;
      if (!video) {
        throw new Error('Scanner video elementが見つかりません');
      }
      video.muted = true;
      video.setAttribute('playsinline', 'true');
      video.srcObject = this.stream;
      await this.playVideoWithTimeout(video);
      this.diagnostics.cameraState = '再生中';
      this.updateMediaDiagnostics();
      this.changeDetector.markForCheck();

      currentPhase = 'decoder-init';
      const decoder = await import('zxing-wasm/reader');
      await decoder.prepareZXingModule({
        overrides: {
          locateFile: () => new URL('assets/zxing-wasm/zxing_reader.wasm', document.baseURI).href,
        },
        fireImmediately: true,
      });
      this.readBarcodes = decoder.readBarcodes;

      if (session === this.scannerSession && this.isOpen) {
        this.scheduleDecode(session, 0);
      }
    } catch (error) {
      if (session !== this.scannerSession || !this.isOpen) {
        return;
      }
      this.diagnostics.cameraState = 'エラー';
      this.updateMediaDiagnostics();
      this.recordScannerError(currentPhase, error);
      this.userErrorMessage = this.getUserFacingError(error, currentPhase);
      this.changeDetector.markForCheck();
    }
  }

  private attachStreamObservers(stream: MediaStream): void {
    stream.addEventListener('inactive', () => {
      this.updateMediaDiagnostics();
      if (!this.isStopping && this.isOpen && this.stream === stream) {
        this.diagnostics.cameraState = 'ストリーム停止';
        this.recordScannerError('camera-start', new Error('MediaStreamが予期せずinactiveになりました'));
      }
    });

    for (const track of stream.getVideoTracks()) {
      track.addEventListener('ended', () => {
        this.updateMediaDiagnostics();
        if (!this.isStopping && this.isOpen && this.stream === stream) {
          this.diagnostics.cameraState = 'トラック終了';
          this.recordScannerError('camera-start', new Error('video trackが予期せずendedになりました'));
        }
      });
    }
  }

  private async playVideoWithTimeout(video: HTMLVideoElement): Promise<void> {
    let timeoutId: number | undefined;
    const timeout = new Promise<never>((_resolve, reject) => {
      timeoutId = window.setTimeout(() => {
        const error = new Error('video.play()後も5秒以内に再生が開始されませんでした');
        error.name = 'VideoPlayTimeoutError';
        reject(error);
      }, 5000);
    });
    try {
      await Promise.race([video.play(), timeout]);
    } finally {
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }
    }
  }

  private scheduleDecode(session: number, delay = this.decodeIntervalMs): void {
    if (session !== this.scannerSession || !this.isOpen) {
      return;
    }
    this.decodeTimer = window.setTimeout(() => void this.decodeFrame(session), delay);
  }

  private async decodeFrame(session: number): Promise<void> {
    if (session !== this.scannerSession || !this.isOpen || this.decodeInProgress) {
      return;
    }

    const video = this.scannerVideo?.nativeElement;
    const canvas = this.scannerCanvas?.nativeElement;
    if (!video || !canvas || !this.readBarcodes) {
      this.recordScannerError('decode', new Error('decoderに必要な要素が初期化されていません'));
      this.scheduleDecode(session);
      return;
    }

    this.updateMediaDiagnostics();
    if (!this.stream?.active || this.stream.getVideoTracks()[0]?.readyState !== 'live') {
      this.recordScannerError('decode', new Error('decode loop実行中にMediaStreamが停止しています'));
      this.scheduleDecode(session);
      return;
    }
    if (video.readyState < 2 || video.videoWidth === 0 || video.videoHeight === 0) {
      this.scheduleDecode(session);
      return;
    }

    this.observeVideoProgress(video);
    this.decodeInProgress = true;
    const startedAt = performance.now();
    this.diagnostics.decodeCount++;
    this.diagnostics.lastDecodeAt = this.now();

    try {
      const scale = Math.min(1, this.maxDecodeDimension / Math.max(video.videoWidth, video.videoHeight));
      canvas.width = Math.max(1, Math.round(video.videoWidth * scale));
      canvas.height = Math.max(1, Math.round(video.videoHeight * scale));
      const context = canvas.getContext('2d', { willReadFrequently: true });
      if (!context) {
        throw new Error('Canvas 2D contextを取得できません');
      }
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
      this.observeBlackFrame(imageData);

      const results = await this.readBarcodes(imageData, this.readerOptionsFallback.options);
      if (session !== this.scannerSession || !this.isOpen) {
        return;
      }

      let confirmedNewCode = false;
      const detectedValuesThisFrame = new Set<string>();
      const detectedAt = Date.now();
      for (const result of results) {
        if (result.error) {
          this.diagnostics.decodeErrorCount++;
          this.recordScannerError('decode', new Error(result.error));
          continue;
        }
        if (!result.isValid || this.scannedCodeSet.has(result.text) || detectedValuesThisFrame.has(result.text)) {
          continue;
        }
        detectedValuesThisFrame.add(result.text);
        const observation = this.confirmationTracker.observe(result.text, result.format, detectedAt);
        if (observation.confirmed) {
          if (this.mode === 'search') {
            const confirmedIdentifier = this.codeSearchSession.acceptConfirmed(result.text);
            if (confirmedIdentifier !== undefined) {
              this.readerOptionsFallback.recordDecodeAttempt(true);
              this.diagnostics.lastDetectionAt = this.now();
              this.showDetectionFeedback();
              this.successSound.play();
              await this.completeCodeSearch(confirmedIdentifier, result.format);
              return;
            }
            continue;
          }
          this.scannedCodeSet.add(result.text);
          this.scannedCodeFormats.set(result.text, result.format);
          this.scannedCodes.update(current => appendConfirmedIdentifier(current, result.text));
          this.successSound.play();
          confirmedNewCode = true;
        }
      }
      this.scanCandidates = this.confirmationTracker.list(detectedAt);
      this.readerOptionsFallback.recordDecodeAttempt(confirmedNewCode);

      if (confirmedNewCode) {
        this.diagnostics.lastDetectionAt = this.now();
        this.showDetectionFeedback();
      }
    } catch (error) {
      this.diagnostics.decodeErrorCount++;
      this.recordScannerError('decode', error);
    } finally {
      const duration = performance.now() - startedAt;
      this.totalDecodeDurationMs += duration;
      this.diagnostics.lastDecodeDurationMs = Math.round(duration);
      this.diagnostics.averageDecodeDurationMs = Math.round(
        this.totalDecodeDurationMs / this.diagnostics.decodeCount,
      );
      this.decodeInProgress = false;
      this.changeDetector.markForCheck();
      this.scheduleDecode(session);
    }
  }

  private observeVideoProgress(video: HTMLVideoElement): void {
    if (video.currentTime === this.lastVideoTime) {
      this.stalledFrameCount++;
      if (this.stalledFrameCount === 8) {
        this.recordScannerError('decode', new Error('decode loopは動作していますがvideo frameが更新されていません'), 'warning');
      }
    } else {
      this.lastVideoTime = video.currentTime;
      this.stalledFrameCount = 0;
    }
  }

  private observeBlackFrame(imageData: ImageData): void {
    let brightnessTotal = 0;
    let samples = 0;
    for (let index = 0; index < imageData.data.length; index += 400) {
      brightnessTotal += imageData.data[index] + imageData.data[index + 1] + imageData.data[index + 2];
      samples++;
    }
    const averageChannelBrightness = samples === 0 ? 0 : brightnessTotal / (samples * 3);
    if (averageChannelBrightness < 2) {
      this.blackFrameCount++;
      if (this.blackFrameCount === 8) {
        this.recordScannerError('decode', new Error('video frameが継続して黒い状態です'), 'warning');
      }
    } else {
      this.blackFrameCount = 0;
    }
  }

  private showDetectionFeedback(): void {
    this.hasRecentDetection = true;
    if (this.detectionFeedbackTimer !== undefined) {
      window.clearTimeout(this.detectionFeedbackTimer);
    }
    this.detectionFeedbackTimer = window.setTimeout(() => {
      this.hasRecentDetection = false;
      this.changeDetector.markForCheck();
    }, 600);
  }

  private async configureZoomCapability(track: MediaStreamTrack | undefined): Promise<void> {
    this.resetZoomCapability();
    if (!track) {
      return;
    }

    if (typeof track.getCapabilities !== 'function') {
      this.diagnostics.zoomCapability = '非対応';
      return;
    }

    const capabilities = track.getCapabilities() as ScannerMediaTrackCapabilities;
    const zoom = capabilities.zoom;
    if (!zoom || !Number.isFinite(zoom.min) || !Number.isFinite(zoom.max) || zoom.max <= zoom.min) {
      this.diagnostics.zoomCapability = '非対応';
      return;
    }

    const settings = track.getSettings() as ScannerMediaTrackSettings;
    this.zoomSupported = true;
    this.zoomMin = zoom.min;
    this.zoomMax = zoom.max;
    this.zoomStep = zoom.step && zoom.step > 0 ? zoom.step : 0.1;
    this.zoomCurrent = this.clampZoom(settings.zoom ?? zoom.min);
    this.diagnostics.zoomCapability = `${this.zoomMin}〜${this.zoomMax} (step ${this.zoomStep})`;
    this.diagnostics.zoomCurrent = String(this.zoomCurrent);

    const initialZoom = resolveInitialScannerZoom(this.readStoredZoom(), this.zoomMin, this.zoomMax);
    await this.applyZoom(initialZoom);
  }

  private async applyZoom(requestedZoom: number, persist = false): Promise<void> {
    if (!this.zoomSupported || !Number.isFinite(requestedZoom)) {
      return;
    }
    const track = this.stream?.getVideoTracks()[0];
    if (!track || track.readyState !== 'live') {
      return;
    }

    const zoom = this.clampZoom(requestedZoom);
    try {
      const zoomConstraint: ScannerZoomConstraint = { zoom };
      await track.applyConstraints({ advanced: [zoomConstraint] });
      const settings = track.getSettings() as ScannerMediaTrackSettings;
      this.zoomCurrent = this.clampZoom(settings.zoom ?? zoom);
      this.diagnostics.zoomCurrent = String(this.zoomCurrent);
      if (persist) {
        this.saveZoom(this.zoomCurrent);
      }
      this.changeDetector.markForCheck();
    } catch (error) {
      const settings = track.getSettings() as ScannerMediaTrackSettings;
      if (Number.isFinite(settings.zoom)) {
        this.zoomCurrent = this.clampZoom(settings.zoom!);
        this.diagnostics.zoomCurrent = String(this.zoomCurrent);
      }
      this.recordScannerError('zoom', error, 'warning');
    }
  }

  private clampZoom(zoom: number): number {
    return clampScannerZoom(zoom, this.zoomMin, this.zoomMax);
  }

  private readStoredZoom(): string | null {
    try {
      return window.localStorage.getItem(this.zoomStorageKey);
    } catch (error) {
      this.recordScannerError('zoom', error, 'warning');
      return null;
    }
  }

  private saveZoom(zoom: number): void {
    try {
      window.localStorage.setItem(this.zoomStorageKey, String(zoom));
    } catch (error) {
      this.recordScannerError('zoom', error, 'warning');
    }
  }

  private resetZoomCapability(): void {
    this.zoomSupported = false;
    this.zoomMin = 1;
    this.zoomMax = 1;
    this.zoomStep = 0.1;
    this.zoomCurrent = 1;
    this.diagnostics.zoomCapability = '未確認';
    this.diagnostics.zoomCurrent = '-';
  }

  private async closeAndDiscardResults(): Promise<void> {
    if (this.closeRequested) {
      return;
    }
    this.closeRequested = true;
    this.clearResults();
    this.registrationError.set('');
    await this.stopScanner('camera-stop');
    this.closed.emit();
  }

  private restoreInitialScans(): void {
    for (const scan of this.initialScans) {
      const value = scan.value?.trim();
      if (!value || this.scannedCodeSet.has(value)) continue;
      this.scannedCodeSet.add(value);
      if (scan.format) this.scannedCodeFormats.set(value, scan.format as ReadResult['format']);
      this.scannedCodes.update(current => appendConfirmedIdentifier(current, value));
    }
  }

  private async closeScannerAfterRegistration(): Promise<void> {
    if (this.closeRequested) {
      return;
    }
    this.closeRequested = true;
    await this.stopScanner('camera-stop');
    this.closed.emit();
  }

  private async completeCodeSearch(identifier: string, format?: ReadResult['format']): Promise<void> {
    if (this.closeRequested) {
      return;
    }
    this.closeRequested = true;
    await this.stopScanner('camera-stop');
    this.closed.emit();
    this.identifierConfirmed.emit(identifier);
    this.identifierScanned.emit({ value: identifier, format });
  }

  private updateMediaDiagnostics(): void {
    const video = this.scannerVideo?.nativeElement;
    const track = this.stream?.getVideoTracks()[0];
    this.diagnostics.videoReadyState = video?.readyState ?? 0;
    this.diagnostics.videoWidth = video?.videoWidth ?? 0;
    this.diagnostics.videoHeight = video?.videoHeight ?? 0;
    this.diagnostics.streamActive = this.stream?.active ?? false;
    this.diagnostics.trackReadyState = track?.readyState ?? 'なし';
  }

  private recordLifecycleEvent(event: string): void {
    const entry = `${this.now()} ${event}`;
    this.diagnostics.lastLifecycleEvent = entry;
    this.lifecycleEvents.unshift(entry);
    if (this.lifecycleEvents.length > 10) {
      this.lifecycleEvents.length = 10;
    }
    this.changeDetector.markForCheck();
  }

  private recordScannerError(
    phase: ScannerPhase,
    error: unknown,
    severity: 'warning' | 'error' = 'error',
  ): void {
    const normalized = this.normalizeError(error);
    this.diagnostics.lastErrorName = normalized.name;
    this.diagnostics.lastErrorMessage = normalized.message;

    const signature = `${phase}|${normalized.name}|${normalized.message}`;
    const timestamp = Date.now();
    const lastRecordedAt = this.recentErrors.get(signature);
    if (lastRecordedAt !== undefined && timestamp - lastRecordedAt < this.errorRateLimitMs) {
      this.diagnostics.suppressedErrorCount++;
      this.changeDetector.markForCheck();
      return;
    }
    this.recentErrors.set(signature, timestamp);

    this.updateMediaDiagnostics();
    const context = {
      timestamp: new Date(timestamp).toISOString(),
      severity,
      screen: this.mode === 'search' ? 'JobList' : 'JobDetail',
      feature: this.mode === 'search' ? 'CodeSearchScanner' : 'LiveCodeScanner',
      phase,
      errorName: normalized.name,
      errorMessage: normalized.message,
      visibilityState: document.visibilityState,
      videoReadyState: this.diagnostics.videoReadyState,
      videoWidth: this.diagnostics.videoWidth,
      videoHeight: this.diagnostics.videoHeight,
      streamActive: this.diagnostics.streamActive,
      trackReadyState: this.diagnostics.trackReadyState,
      decodeCount: this.diagnostics.decodeCount,
      decodeErrorCount: this.diagnostics.decodeErrorCount,
      lastDecodeDurationMs: this.diagnostics.lastDecodeDurationMs,
      cameraRestartCount: this.diagnostics.restartCount,
      zoomSupported: this.zoomSupported,
      zoomCurrent: this.zoomSupported ? this.zoomCurrent : null,
    };
    this.dataService.setErrorLog(JSON.stringify(context));
    this.changeDetector.markForCheck();
  }

  private normalizeError(error: unknown): { name: string; message: string } {
    if (error instanceof DOMException || error instanceof Error) {
      return { name: error.name || 'Error', message: error.message || '詳細不明' };
    }
    return { name: 'UnknownError', message: String(error) };
  }

  private getUserFacingError(error: unknown, phase: ScannerPhase): string {
    const normalized = this.normalizeError(error);
    if (normalized.name === 'NotAllowedError') {
      return 'カメラの利用が許可されませんでした。iPhoneのSafari/PWA設定でカメラ利用を許可してください。';
    }
    if (normalized.name === 'NotReadableError') {
      return 'カメラを開始できませんでした。他のアプリがカメラを使用していないか確認してください。';
    }
    if (normalized.name === 'OverconstrainedError') {
      return '利用可能なカメラが指定条件に対応していません。';
    }
    if (normalized.name === 'SecurityError' || normalized.name === 'NotSupportedError') {
      return normalized.message;
    }
    if (phase === 'decoder-init') {
      return 'コード読み取り機能を初期化できませんでした。エラー表示タブを確認してください。';
    }
    return 'カメラを使用できませんでした。エラー表示タブを確認してください。';
  }

  private clearTimers(): void {
    if (this.decodeTimer !== undefined) {
      window.clearTimeout(this.decodeTimer);
      this.decodeTimer = undefined;
    }
    if (this.visibilityCheckTimer !== undefined) {
      window.clearTimeout(this.visibilityCheckTimer);
      this.visibilityCheckTimer = undefined;
    }
    if (this.detectionFeedbackTimer !== undefined) {
      window.clearTimeout(this.detectionFeedbackTimer);
      this.detectionFeedbackTimer = undefined;
    }
  }

  private now(): string {
    return new Date().toLocaleTimeString('ja-JP');
  }
}
